import {
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Input, notification, Select, Space } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDownloadCSV } from '../../../../../../utils/api/mpCatalogue/useDownloadCSV';
import { useGetMpCatalogues } from '../../../../../../utils/api/mpCatalogue/useGetMpCatalogue';
import { useRemoveFromMpCategories } from '../../../../../../utils/api/mpCategories/useRemoveFromMpCategories';
import { useGetStores } from '../../../../../../utils/api/stores/useGetStores';
import { useCurrentMarketplace } from '../../../../../../utils/zustand';
import MpProductTable from '../../../../mpProducts/list/MpProductTable';
import RemoveProductsModal from './RemoveProductsModal';

const MarketplaceCategoryProducts = ({ category }) => {
  const { marketplace } = useParams();
  const { t } = useTranslation('products');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { currentMarketplace } = useCurrentMarketplace();
  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  const storesParams = useMemo(
    () => ({ marketplace__name: marketplace }),
    [marketplace]
  );

  const catalogueParams = useMemo(
    () => ({
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      search: search || undefined,
      mp_category: category?.id,
      store: selectedStore,
    }),
    [currentPage, pageSize, search, category?.id, selectedStore]
  );

  const { stores, isLoading: isLoadingStores } = useGetStores(storesParams);
  const { mpCatalogues, count, isLoading } = useGetMpCatalogues(
    catalogueParams,
    {
      enabled: !!(category?.id || category?.code),
    }
  );
  const { downloadCSV, isLoading: isDownloadingCSV } = useDownloadCSV();
  const { removeProductsFromMpCategory, isLoading: isRemovingProducts } =
    useRemoveFromMpCategories();

  const actionsDisabled = useMemo(() => {
    return !selectedStore;
  }, [selectedStore]);

  const handleExport = async () => {
    const body = {
      mp_category: category?.id,
      store: selectedStore,
      marketplace: currentMarketplace?.id,
    };
    try {
      const result = await downloadCSV(body);
      if (result.async) {
        notification.success({
          message: 'Export en cours',
          description: "Vous recevrez un email lorsque l'export sera terminé.",
          placement: 'bottomRight',
          duration: 5,
        });
      } else {
        notification.success({
          message: 'Export réussi',
          description: 'Le fichier a été exporté avec succès',
          placement: 'bottomRight',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: "Erreur d'export",
        description: error.message,
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleSelectedExport = async () => {
    const products = selectedProducts.map(
      (product) => product.product.reference
    );
    const body = {
      mp_category: category?.id,
      store: selectedStore,
      marketplace: currentMarketplace?.id,
      products,
    };
    try {
      const result = await downloadCSV(body);
      if (result.async) {
        notification.success({
          message: 'Export en cours',
          description: "Vous recevrez un email lorsque l'export sera terminé.",
          placement: 'bottomRight',
          duration: 5,
        });
      } else {
        notification.success({
          message: 'Export réussi',
          description: 'Le fichier a été exporté avec succès',
          placement: 'bottomRight',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: "Erreur d'export",
        description: error.message,
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleStoreChange = useCallback((value) => {
    setSelectedStore(value);
    setCurrentPage(1); // Reset to first page when filter changes
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleOpenRemoveModal = useCallback(() => {
    setRemoveModalOpen(true);
  }, []);

  const handleCloseRemoveModal = useCallback(() => {
    setRemoveModalOpen(false);
  }, []);

  const handleConfirmRemove = async () => {
    try {
      const categoryId = category?.id;
      const storeId = selectedStore;
      const body = {
        products: selectedProducts.map(
          (mpProduct) => mpProduct.product.reference
        ),
        stores: [storeId],
      };
      await removeProductsFromMpCategory({ categoryId, body });
      notification.success({
        message: 'Retrait de la catégorie réussi',
        description:
          'Les produits ont été retirés de la catégorie avec succès.',
      });
      handleCloseRemoveModal();
      setSelectedProducts([]);
    } catch (error) {
      console.error('Erreur lors du retrait des produits:', error);
      notification.error({
        message: 'Erreur de retrait des produits',
        description: error.message,
        placement: 'bottomRight',
        duration: 3,
      });
      handleCloseRemoveModal();
    }
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <Space>
          <Input
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            placeholder={t('filters.search_placeholder')}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            allowClear
            size="small"
            placeholder="Sélectionner un store"
            loading={isLoadingStores}
            disabled={isLoadingStores}
            value={selectedStore}
            showSearch={true}
            style={{ width: 300, marginLeft: '8px' }}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleStoreChange}
            options={stores?.map((store) => ({
              label: store.name,
              value: store.id,
            }))}
          />
        </Space>
        {selectedProducts.length > 0 ? (
          <Space>
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              disabled={actionsDisabled}
              onClick={handleOpenRemoveModal}
              loading={isDownloadingCSV}
            >
              Retirer ({selectedProducts.length})
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="small"
              disabled={actionsDisabled}
              onClick={handleSelectedExport}
              loading={isDownloadingCSV}
            >
              Exporter ({selectedProducts.length})
            </Button>
          </Space>
        ) : (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="small"
            disabled={actionsDisabled}
            onClick={handleExport}
            loading={isDownloadingCSV}
          >
            Tout exporter
          </Button>
        )}
      </div>
      <MpProductTable
        hasSelection={true}
        stickyHeader={false}
        hasActions={false}
        selectedProducts={selectedProducts}
        updateSelectedMpProducts={setSelectedProducts}
        mpCatalogues={mpCatalogues}
        count={count}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        listHeight={550}
      />
      <RemoveProductsModal
        open={removeModalOpen}
        onClose={handleCloseRemoveModal}
        onConfirm={handleConfirmRemove}
        selectedProducts={selectedProducts}
        loading={isRemovingProducts}
      />
    </div>
  );
};

export default MarketplaceCategoryProducts;
